import {
  type Appointment,
  HANDOVER_TYPE_LABELS,
  type HandoverApartmentListResponse,
  type MeResponse,
  type ProjectTeamResponse,
  type ScheduleResponse,
  addDays,
  appointmentSchema,
  calendarDate,
  handoverApartmentListResponseSchema,
  projectAdminListResponseSchema,
  projectTeamResponseSchema,
  scheduleResponseSchema,
  startOfDayIn,
  timeOfDayIn,
} from '@sivan/shared';
import { type ReactNode, useState } from 'react';
import { apiGet, apiPost } from '../api/client';
import { useCurrentProject } from '../current-project';
import { formatLocation } from '../handover/location';
import { type Loadable, useLoad, useWorkspace } from '../master-data/use-master-data';
import { explainScheduleError } from '../schedule/ScheduleScreen';
import { AppLink } from '../ui/AppLink';
import { readinessChecks } from '../master-data/project-setup';
import { Icon } from '../ui/Icon';
import { ProgressRing } from '../ui/ProgressRing';
import {
  type HomeData,
  type HomeRoles,
  type OpenDraft,
  type ProjectStatus,
  draftOf,
  projectStatus,
  greeting,
  homeData,
  isMine,
  keysIn,
  primaryOf,
  rolesOf,
} from './home-model';

/** The most urgent tasks first; the rest behind one button, so Home stays short. */
const TASKS_SHOWN = 5;

// "הבית שלי" (Gate 2B): the user's work in the current project. Each part is shown only when the
// user's permissions call for it and its data loaded; a part that failed says so on its own and
// the rest stays usable.

interface Project {
  readonly id: string;
  readonly name: string;
  readonly code: string;
}

/**
 * The latest data of a source: while it reloads (after an action on Home), the previous data stays
 * on screen instead of a loading placeholder, so nothing jumps and messages stay visible.
 */
function useLatest<T>(state: Loadable<T>): { readonly data: T | null; readonly loaded: boolean } {
  const [last, setLast] = useState<{ readonly data: T | null } | null>(null);
  if (state.state === 'ready' && last?.data !== state.data) setLast({ data: state.data });
  if (state.state === 'ready') return { data: state.data, loaded: true };
  return { data: last?.data ?? null, loaded: last !== null };
}

function apartmentText(a: { readonly number: string; readonly building: string }) {
  return `דירה ${a.number} · בניין ${a.building}`;
}

/** "היום 09:00", "מחר 11:30" or "17/10 14:00" for an appointment, in Israel time. */
function whenText(iso: string, today: string): string {
  const day = calendarDate(new Date(iso));
  const time = timeOfDayIn(new Date(iso));
  if (day === today) return `היום ${time}`;
  if (day === addDays(today, 1)) return `מחר ${time}`;
  const [, m, d] = day.split('-');
  return `${d ?? ''}/${m ?? ''} ${time}`;
}

const handoverLink = (projectId: string, apartmentId: string, handoverId: string | null) =>
  formatLocation({ projectId, apartmentId, handoverId });

export function HomeScreen() {
  const [workspace, reload] = useWorkspace();
  const projects = workspace.state === 'ready' ? workspace.data.projects : [];
  const project = useCurrentProject(projects);
  if (workspace.state === 'loading') return <HomeSkeleton />;
  if (workspace.state !== 'ready')
    return (
      <div className="notice notice--error" role="alert">
        <p>טעינת הנתונים שלך נכשלה.</p>
        <button type="button" className="button" onClick={reload}>
          נסו שוב
        </button>
      </div>
    );
  const { me } = workspace.data;
  if (project === undefined)
    return (
      <div className="home">
        <Hello me={me} />
        <section className="home-card home-empty">
          <h2>אין פרויקטים זמינים עבורך</h2>
          <p className="hint">
            {me.systemOwner
              ? 'לבעלי המערכת אין גישה לנתונים עסקיים. הרשאות לפרויקטים ניתנות לעובדים בצוות הפרויקט.'
              : 'כשתצורפו לצוות של פרויקט, המסירות והיומן שלו יופיעו כאן.'}
          </p>
        </section>
      </div>
    );
  return <ProjectHome key={project.id} me={me} project={project} />;
}

function Hello({ me, children }: { readonly me: MeResponse; readonly children?: ReactNode }) {
  const now = new Date();
  const date = new Intl.DateTimeFormat('he-IL', {
    timeZone: 'Asia/Jerusalem',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(now);
  return (
    <div className="home-hello">
      <div>
        <p className="home-hello__date">{date}</p>
        <h2 className="home-hello__title">
          {greeting(now)}, {me.user.displayName}
        </h2>
      </div>
      {children}
    </div>
  );
}

function HomeSkeleton() {
  return (
    <div className="home" role="status" aria-label="טוען את הבית שלי">
      <div className="skeleton__row home-skeleton home-skeleton--title" />
      <div className="skeleton__row home-skeleton home-skeleton--card" />
      <div className="skeleton__row home-skeleton" />
      <div className="skeleton__row home-skeleton" />
    </div>
  );
}

function ProjectHome({ me, project }: { readonly me: MeResponse; readonly project: Project }) {
  const keys = keysIn(me, project.id);
  const roles = rolesOf(keys);
  const [version, setVersion] = useState(0);
  const now = new Date();
  const today = calendarDate(now);
  const from = startOfDayIn(today).toISOString();
  const to = startOfDayIn(addDays(today, 8)).toISOString();
  const seesSchedule = keys.has('schedule.calendar.view');
  const seesHandovers = keys.has('handover.handover.view');
  const seesTeam = roles.admin && keys.has('projects.team.view');
  const [schedule, reloadSchedule] = useLoad(
    `home-schedule:${project.id}:${today}:${String(version)}`,
    () =>
      seesSchedule
        ? apiGet(
            `/projects/${project.id}/schedule?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
            scheduleResponseSchema,
          )
        : Promise.resolve(null),
  );
  const [handovers, reloadHandovers] = useLoad(
    `home-handovers:${project.id}:${String(version)}`,
    () =>
      seesHandovers
        ? apiGet(`/projects/${project.id}/handover-apartments`, handoverApartmentListResponseSchema)
        : Promise.resolve(null),
  );
  const [team, reloadTeam] = useLoad(`home-team:${project.id}:${String(version)}`, () =>
    seesTeam
      ? apiGet(`/projects/${project.id}/team`, projectTeamResponseSchema)
      : Promise.resolve(null),
  );
  const latestSchedule = useLatest(schedule);
  const latestHandovers = useLatest(handovers);
  const latestTeam = useLatest(team);
  // The placeholder only before the first answer of each source.
  const loading =
    (schedule.state === 'loading' && !latestSchedule.loaded) ||
    (handovers.state === 'loading' && !latestHandovers.loaded) ||
    (team.state === 'loading' && !latestTeam.loaded);
  const scheduleData = latestSchedule.data;
  const handoverData = latestHandovers.data;
  const teamData = latestTeam.data;
  const data = homeData(scheduleData, handoverData, teamData, now);
  const failed = [
    schedule.state === 'error' ? { text: 'היומן', retry: reloadSchedule } : null,
    handovers.state === 'error' ? { text: 'המסירות', retry: reloadHandovers } : null,
    team.state === 'error' ? { text: 'הצוות', retry: reloadTeam } : null,
  ].filter((f) => f !== null);
  const refresh = () => {
    setVersion((v) => v + 1);
  };

  const stats: [number, string][] = [];
  if (scheduleData !== null) {
    stats.push([data.today.length, data.today.length === 1 ? 'מסירה היום' : 'מסירות היום']);
    if (roles.field && scheduleData.me !== null)
      stats.push([data.mine.length, data.mine.length === 1 ? 'משובצת לך' : 'משובצות לך']);
    if (roles.coordinator) stats.push([data.requests.length, 'בקשות שיבוץ']);
  }
  if (handoverData !== null) stats.push([data.incomplete.length, 'מסירות בעבודה']);

  return (
    <div className="home" aria-busy={loading}>
      <Hello me={me}>
        {stats.length > 0 && (
          <ul className="home-stats" aria-label="היום במספרים" tabIndex={0}>
            {stats.map(([n, text]) => (
              <li key={text} className="home-stat">
                <b>{n}</b> {text}
              </li>
            ))}
          </ul>
        )}
      </Hello>
      {failed.map((f) => (
        <div key={f.text} className="notice notice--error home-failed" role="alert">
          <p>טעינת {f.text} נכשלה. שאר הנתונים מוצגים.</p>
          <button type="button" className="button button--small" onClick={f.retry}>
            נסו שוב
          </button>
        </div>
      ))}
      {loading ? (
        <HomeSkeleton />
      ) : (
        <div className="home-grid">
          <div className="home-main">
            <NextCard
              project={project}
              roles={roles}
              data={data}
              schedule={scheduleData}
              handovers={handoverData}
              team={teamData}
              today={today}
            />
            {handoverData !== null && (roles.manager || roles.admin || !roles.field) && (
              <StatusCard project={project} status={projectStatus(handoverData)} />
            )}
            {scheduleData !== null && (
              <TodayList
                project={project}
                roles={roles}
                data={data}
                schedule={scheduleData}
                handovers={handoverData}
              />
            )}
          </div>
          <div className="home-side">
            <Tasks
              project={project}
              roles={roles}
              data={data}
              schedule={scheduleData}
              handovers={handoverData}
              today={today}
              onChanged={refresh}
            />
            {roles.field && handoverData !== null && (
              <Drafts project={project} drafts={data.incomplete} />
            )}
            {scheduleData !== null && (
              <Upcoming
                list={
                  roles.field && scheduleData.me !== null && !roles.coordinator
                    ? data.upcoming.filter((a) => isMine(a, scheduleData.me?.employeeId ?? null))
                    : data.upcoming
                }
                today={today}
              />
            )}
            {roles.admin && keys.has('organization.project.create') && (
              <ReadinessCard projectId={project.id} />
            )}
            {roles.admin && <Shortcuts keys={keys} />}
          </div>
        </div>
      )}
    </div>
  );
}

function NextCard({
  project,
  roles,
  data,
  schedule,
  handovers,
  team,
  today,
}: {
  readonly project: Project;
  readonly roles: HomeRoles;
  readonly data: HomeData;
  readonly schedule: ScheduleResponse | null;
  readonly handovers: HandoverApartmentListResponse | null;
  readonly team: ProjectTeamResponse | null;
  readonly today: string;
}) {
  const mine = roles.field ? data.mine[0] : undefined;
  if (mine !== undefined) {
    const draft = draftOf(mine, handovers);
    const handoverId = mine.handoverId ?? draft?.id ?? null;
    const progress = draft?.progress ?? null;
    return (
      <section className="home-next" aria-labelledby="home-next-title">
        {progress !== null && progress.total > 0 && (
          <ProgressRing
            done={progress.checked}
            total={progress.total}
            size={72}
            label={`נבדקו ${String(progress.checked)} מתוך ${String(progress.total)}`}
          />
        )}
        <div className="home-next__text">
          <p className="home-next__eyebrow">המסירה הבאה שלך · {whenText(mine.startsAt, today)}</p>
          <h2 className="home-next__title" id="home-next-title">
            {apartmentText(mine.apartment)}
          </h2>
          <p className="home-next__meta">
            {HANDOVER_TYPE_LABELS[mine.type]}
            {mine.apartment.floor !== '' && ` · ${mine.apartment.floor}`}
            {progress !== null &&
              progress.openFindings > 0 &&
              ` · ${progress.openFindings === 1 ? 'ליקוי פתוח אחד' : `${String(progress.openFindings)} ליקויים פתוחים`}`}
          </p>
        </div>
        <div className="home-next__actions">
          {handoverId !== null ? (
            <AppLink
              className="button button--signal"
              to={handoverLink(project.id, mine.apartment.id, handoverId)}
            >
              {draft !== undefined && (draft.progress?.checked ?? 0) > 0
                ? 'המשך במסירה'
                : 'פתיחת המסירה'}
              <Icon name="arrow-left" />
            </AppLink>
          ) : handovers?.permissions.create === true ? (
            <AppLink
              className="button button--signal"
              to={handoverLink(project.id, mine.apartment.id, null)}
            >
              לדירה ולפתיחת המסירה
              <Icon name="arrow-left" />
            </AppLink>
          ) : null}
          <AppLink className="button button--on-dark" to="#schedule">
            ביומן
          </AppLink>
        </div>
      </section>
    );
  }
  const resume = roles.field ? data.incomplete[0] : undefined;
  if (resume !== undefined)
    return (
      <section className="home-next" aria-labelledby="home-next-title">
        {resume.handover.progress !== null && resume.handover.progress.total > 0 && (
          <ProgressRing
            done={resume.handover.progress.checked}
            total={resume.handover.progress.total}
            size={72}
            label={`נבדקו ${String(resume.handover.progress.checked)} מתוך ${String(resume.handover.progress.total)}`}
          />
        )}
        <div className="home-next__text">
          <p className="home-next__eyebrow">להמשיך מאיפה שהפסקת</p>
          <h2 className="home-next__title" id="home-next-title">
            דירה {resume.apartment.apartmentNumber} · בניין {resume.apartment.building}
          </h2>
          <p className="home-next__meta">
            {HANDOVER_TYPE_LABELS[resume.handover.type]} · {resume.handover.protocolNumber}
          </p>
        </div>
        <div className="home-next__actions">
          <AppLink
            className="button button--signal"
            to={handoverLink(project.id, resume.apartment.id, resume.handover.id)}
          >
            המשך במסירה
            <Icon name="arrow-left" />
          </AppLink>
        </div>
      </section>
    );
  if (
    roles.coordinator &&
    schedule !== null &&
    (data.requests.length > 0 || data.unassigned.length > 0)
  )
    return (
      <section className="home-next" aria-labelledby="home-next-title">
        <span className="home-next__count" aria-hidden="true">
          {data.requests.length + data.unassigned.length}
        </span>
        <div className="home-next__text">
          <p className="home-next__eyebrow">שיבוצים · {project.name}</p>
          <h2 className="home-next__title" id="home-next-title">
            {data.requests.length > 0
              ? `${data.requests.length === 1 ? 'בקשת שיבוץ אחת ממתינה' : `${String(data.requests.length)} בקשות שיבוץ ממתינות`} להחלטה שלך`
              : `${data.unassigned.length === 1 ? 'מסירה אחת' : `${String(data.unassigned.length)} מסירות`} עדיין ללא אחראי`}
          </h2>
          <p className="home-next__meta">
            {data.unassigned.length > 0 && data.requests.length > 0
              ? `ועוד ${String(data.unassigned.length)} מסירות ללא אחראי`
              : 'ההחלטה מתעדכנת ביומן ומודיעה לצוות.'}
          </p>
        </div>
        <div className="home-next__actions">
          <a className="button button--signal" href="#home-tasks">
            לטיפול
            <Icon name="arrow-left" />
          </a>
          <AppLink className="button button--on-dark" to="#schedule">
            ליומן
          </AppLink>
        </div>
      </section>
    );
  if (roles.admin)
    return (
      <section className="home-next" aria-labelledby="home-next-title">
        <div className="home-next__text">
          <p className="home-next__eyebrow">סקירת פרויקט</p>
          <h2 className="home-next__title" id="home-next-title">
            {project.name}
          </h2>
          <dl className="home-overview">
            {handovers !== null && (
              <>
                <div>
                  <dt>דירות</dt>
                  <dd>{handovers.items.length}</dd>
                </div>
                <div>
                  <dt>מסירות בעבודה</dt>
                  <dd>{data.incomplete.length}</dd>
                </div>
              </>
            )}
            {schedule !== null && (
              <div>
                <dt>מסירות השבוע</dt>
                <dd>{data.today.length + data.upcoming.length}</dd>
              </div>
            )}
            {team !== null && (
              <div>
                <dt>בצוות</dt>
                <dd>{team.members.length}</dd>
              </div>
            )}
          </dl>
        </div>
      </section>
    );
  return (
    <section className="home-card home-empty" aria-labelledby="home-next-title">
      <h2 id="home-next-title">
        {roles.field ? 'אין משימות פתוחות כרגע' : `${project.name}: צפייה`}
      </h2>
      <p className="hint">
        {roles.field
          ? 'כשתשובצו למסירה או שתיפתח טיוטה, היא תופיע כאן.'
          : 'המסירות והמועדים של הפרויקט מוצגים כאן לצפייה. שינויים נעשים על ידי בעלי ההרשאה.'}
      </p>
    </section>
  );
}

function TodayList({
  project,
  roles,
  data,
  schedule,
  handovers,
}: {
  readonly project: Project;
  readonly roles: HomeRoles;
  readonly data: HomeData;
  readonly schedule: ScheduleResponse;
  readonly handovers: HandoverApartmentListResponse | null;
}) {
  const employeeId = schedule.me?.employeeId ?? null;
  return (
    <section className="home-section" aria-labelledby="home-today-title">
      <div className="home-section__head">
        <h2 id="home-today-title">
          {roles.coordinator ? 'מסירות היום בפרויקט' : 'המסירות של היום'}
        </h2>
        <AppLink to="#schedule" className="home-section__more">
          ליומן
        </AppLink>
      </div>
      {data.today.length === 0 ? (
        <p className="home-card home-empty-line">אין מסירות היום.</p>
      ) : (
        <ul className="home-card home-list">
          {data.today.map((a) => {
            const mine = isMine(a, employeeId);
            const primary = primaryOf(a);
            const pending = a.assignments.some((s) => s.status === 'requested');
            const draft = draftOf(a, handovers);
            const handoverId = a.handoverId ?? draft?.id ?? null;
            return (
              <li key={a.id} className={`home-appt${mine ? ' home-appt--mine' : ''}`}>
                <span className="home-appt__time">{timeOfDayIn(new Date(a.startsAt))}</span>
                <span className="home-appt__what">
                  <b>{apartmentText(a.apartment)}</b>
                  <span className="hint">
                    {HANDOVER_TYPE_LABELS[a.type]}
                    {primary !== undefined && !mine && ` · ${primary.employee.fullName}`}
                  </span>
                </span>
                <span className="home-appt__state">
                  {a.status === 'completed' ? (
                    <span className="chip chip--done">בוצע</span>
                  ) : mine ? (
                    <span className="chip chip--mine">משובצת לך</span>
                  ) : primary === undefined ? (
                    <span className={`chip ${pending ? 'chip--pending' : 'chip--alert'}`}>
                      {pending ? 'בקשה ממתינה' : 'ללא אחראי'}
                    </span>
                  ) : (
                    <span className="chip">משובץ</span>
                  )}
                </span>
                <span className="home-appt__action">
                  {handoverId !== null && (roles.field || mine) ? (
                    <AppLink
                      className={`button button--small ${mine ? 'button--primary' : ''}`}
                      to={handoverLink(project.id, a.apartment.id, handoverId)}
                      aria-label={`${mine ? 'המשך' : 'פתיחה'}: ${apartmentText(a.apartment)}`}
                    >
                      {mine ? 'המשך' : 'פתיחה'}
                    </AppLink>
                  ) : roles.coordinator && primary === undefined ? (
                    <AppLink
                      className="button button--small"
                      to="#schedule"
                      aria-label={`שיבוץ ביומן: ${apartmentText(a.apartment)}`}
                    >
                      שיבוץ
                    </AppLink>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function Tasks({
  project,
  roles,
  data,
  schedule,
  handovers,
  today,
  onChanged,
}: {
  readonly project: Project;
  readonly roles: HomeRoles;
  readonly data: HomeData;
  readonly schedule: ScheduleResponse | null;
  readonly handovers: HandoverApartmentListResponse | null;
  readonly today: string;
  readonly onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    readonly kind: 'ok' | 'error';
    readonly text: string;
  } | null>(null);
  const decide = async (assignmentId: string, approve: boolean, who: string) => {
    setBusy(assignmentId);
    setMessage(null);
    try {
      await apiPost(
        `/projects/${project.id}/schedule/assignments/${assignmentId}/${approve ? 'approve' : 'reject'}`,
        {},
        appointmentSchema,
      );
      setMessage({ kind: 'ok', text: `${who}: ${approve ? 'הבקשה אושרה' : 'הבקשה נדחתה'}.` });
      onChanged();
    } catch (error) {
      setMessage({ kind: 'error', text: explainScheduleError(error) });
    } finally {
      setBusy(null);
    }
  };
  const [all, setAll] = useState(false);
  const canApprove = schedule?.permissions.approve === true;
  const items: ReactNode[] = [];
  if (roles.coordinator && schedule !== null) {
    for (const { assignment, appointment } of data.requests)
      items.push(
        <li key={assignment.id} className="home-task home-task--request">
          <p className="home-task__title">
            {assignment.employee.fullName} מבקש/ת לבצע: {apartmentText(appointment.apartment)}
          </p>
          <p className="hint">
            {HANDOVER_TYPE_LABELS[appointment.type]} · {whenText(appointment.startsAt, today)} ·{' '}
            {assignment.role === 'primary' ? 'כאחראי/ת' : 'כמלווה'}
          </p>
          {canApprove && (
            <div className="home-task__actions">
              <button
                type="button"
                className="button button--primary button--small"
                aria-busy={busy === assignment.id}
                disabled={busy !== null}
                aria-label={`אישור הבקשה של ${assignment.employee.fullName}`}
                onClick={() => {
                  void decide(assignment.id, true, assignment.employee.fullName);
                }}
              >
                אישור
              </button>
              <button
                type="button"
                className="button button--small"
                disabled={busy !== null}
                aria-label={`דחיית הבקשה של ${assignment.employee.fullName}`}
                onClick={() => {
                  void decide(assignment.id, false, assignment.employee.fullName);
                }}
              >
                דחייה
              </button>
            </div>
          )}
        </li>,
      );
    for (const appointment of data.unassigned.filter(
      (a) => !a.assignments.some((s) => s.status === 'requested'),
    ))
      items.push(
        <li key={`u:${appointment.id}`} className="home-task home-task--alert">
          <p className="home-task__title">ללא אחראי: {apartmentText(appointment.apartment)}</p>
          <p className="hint">
            {HANDOVER_TYPE_LABELS[appointment.type]} · {whenText(appointment.startsAt, today)}
          </p>
          <div className="home-task__actions">
            <AppLink className="button button--small" to="#schedule">
              שיבוץ ביומן
            </AppLink>
          </div>
        </li>,
      );
  }
  if (roles.field && handovers !== null)
    for (const { apartment, handover } of data.attention.slice(0, 5)) {
      const p = handover.progress;
      if (p === null) continue;
      items.push(
        <li key={`f:${handover.id}`} className="home-task home-task--finding">
          <p className="home-task__title">
            דירה {apartment.apartmentNumber}:{' '}
            {[
              p.openFindings > 0
                ? p.openFindings === 1
                  ? 'ליקוי פתוח אחד'
                  : `${String(p.openFindings)} ליקויים פתוחים`
                : null,
              p.awaitingReinspection > 0 ? `${String(p.awaitingReinspection)} לבדיקה חוזרת` : null,
            ]
              .filter((t) => t !== null)
              .join(' · ')}
          </p>
          <p className="hint">
            {HANDOVER_TYPE_LABELS[handover.type]} · {handover.protocolNumber}
          </p>
          <div className="home-task__actions">
            <AppLink
              className="button button--small"
              to={handoverLink(project.id, apartment.id, handover.id)}
            >
              פתיחה
            </AppLink>
          </div>
        </li>,
      );
    }
  if (roles.admin)
    for (const member of data.teamIssues.slice(0, 5))
      items.push(
        <li key={`t:${member.employee.id}`} className="home-task home-task--team">
          <p className="home-task__title">{member.employee.fullName}</p>
          <p className="hint">
            {member.employee.status !== 'active'
              ? 'עובד/ת לא פעיל/ה בצוות הפרויקט'
              : member.employee.account.state === 'none'
                ? 'אין חשבון כניסה: לא יכול/ה לבצע מסירות'
                : 'החשבון ממתין לאימות זהות'}
          </p>
          <div className="home-task__actions">
            <AppLink className="button button--small" to="#team">
              לצוות
            </AppLink>
          </div>
        </li>,
      );
  return (
    <section
      className="home-section"
      id="home-tasks"
      tabIndex={-1}
      aria-labelledby="home-tasks-title"
    >
      <div className="home-section__head">
        <h2 id="home-tasks-title">
          ממתין לך <span className="count">({items.length})</span>
        </h2>
      </div>
      {message !== null && (
        <p
          className={`notice ${message.kind === 'ok' ? 'notice--success' : 'notice--error'}`}
          role={message.kind === 'ok' ? 'status' : 'alert'}
        >
          {message.text}
        </p>
      )}
      {items.length === 0 ? (
        <p className="home-card home-empty-line">אין משימות שממתינות לך.</p>
      ) : (
        <>
          <ul className="home-card home-tasks">{all ? items : items.slice(0, TASKS_SHOWN)}</ul>
          {items.length > TASKS_SHOWN && (
            <button
              type="button"
              className="button button--ghost home-more"
              aria-expanded={all}
              onClick={() => {
                setAll((v) => !v);
              }}
            >
              {all ? 'הצגת פחות' : `הצגת כל ${String(items.length)} המשימות`}
            </button>
          )}
        </>
      )}
    </section>
  );
}

function Drafts({
  project,
  drafts,
}: {
  readonly project: Project;
  readonly drafts: readonly OpenDraft[];
}) {
  return (
    <section className="home-section" aria-labelledby="home-drafts-title">
      <div className="home-section__head">
        <h2 id="home-drafts-title">
          מסירות בעבודה <span className="count">({drafts.length})</span>
        </h2>
        <AppLink
          to={formatLocation({ projectId: project.id, apartmentId: null, handoverId: null })}
          className="home-section__more"
        >
          לכל הדירות
        </AppLink>
      </div>
      {drafts.length === 0 ? (
        <p className="home-card home-empty-line">אין מסירות עם פריטים שטרם נבדקו.</p>
      ) : (
        <ul className="home-card home-list">
          {drafts.slice(0, 5).map(({ apartment, handover }) => (
            <li key={handover.id} className="home-draft">
              {handover.progress !== null && handover.progress.total > 0 ? (
                <ProgressRing
                  done={handover.progress.checked}
                  total={handover.progress.total}
                  size={36}
                />
              ) : (
                <Icon name="file" className="home-draft__icon" />
              )}
              <span className="home-draft__what">
                <b>
                  דירה {apartment.apartmentNumber} · בניין {apartment.building}
                </b>
                <span className="hint">
                  {HANDOVER_TYPE_LABELS[handover.type]} ·{' '}
                  {handover.progress === null
                    ? 'הבדיקה טרם התחילה'
                    : `נבדקו ${String(handover.progress.checked)} מתוך ${String(handover.progress.total)}`}
                </span>
              </span>
              <AppLink
                className="button button--small"
                to={handoverLink(project.id, apartment.id, handover.id)}
                aria-label={`המשך: דירה ${apartment.apartmentNumber}, ${HANDOVER_TYPE_LABELS[handover.type]}`}
              >
                המשך
              </AppLink>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Upcoming({
  list,
  today,
}: {
  readonly list: readonly Appointment[];
  readonly today: string;
}) {
  if (list.length === 0) return null;
  return (
    <section className="home-section" aria-labelledby="home-upcoming-title">
      <div className="home-section__head">
        <h2 id="home-upcoming-title">הבא בתור</h2>
      </div>
      <ul className="home-card home-list">
        {list.slice(0, 5).map((a) => (
          <li key={a.id} className="home-upcoming">
            <b className="home-upcoming__when">{whenText(a.startsAt, today)}</b>
            <span>
              {apartmentText(a.apartment)} · {HANDOVER_TYPE_LABELS[a.type]}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Shortcuts({ keys }: { readonly keys: ReadonlySet<string> }) {
  const links: [string, string, string][] = [];
  if (keys.has('projects.team.view') || keys.has('organization.employee.view'))
    links.push(['#team', 'צוות והרשאות', 'עובדים, חברות בצוות ותפקידים']);
  if (keys.has('schedule.calendar.view'))
    links.push(['#schedule', 'יומן מסירות', 'מועדים ושיבוצים']);
  if (
    keys.has('organization.project.create') ||
    keys.has('projects.project.edit') ||
    keys.has('projects.unit.view')
  )
    links.push(['#master-data', 'ניהול נתוני אב', 'פרויקטים, בניינים, דירות והגדרות מסירה']);
  if (links.length === 0) return null;
  return (
    <section className="home-section" aria-labelledby="home-admin-title">
      <div className="home-section__head">
        <h2 id="home-admin-title">ניהול</h2>
      </div>
      <ul className="home-card home-list">
        {links.map(([to, label, hint]) => (
          <li key={to}>
            <AppLink className="home-shortcut" to={to}>
              <span>
                <b>{label}</b>
                <span className="hint">{hint}</span>
              </span>
              <Icon name="chevron-left" />
            </AppLink>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The project at a glance (managers, administrators, viewers): handovers and what needs action. */
function StatusCard({
  project,
  status,
}: {
  readonly project: Project;
  readonly status: ProjectStatus;
}) {
  const action = status.refused + status.awaitingReinspection;
  return (
    <section className="home-section" aria-labelledby="home-status-title">
      <div className="home-section__head">
        <h2 id="home-status-title">מצב המסירות בפרויקט</h2>
        <AppLink to={`#handover/${project.id}`}>לכל הדירות</AppLink>
      </div>
      <div className="home-card home-status-card">
        {status.apartments > 0 && (
          // The project's apartments by stage, as one bar; the numbers below say the same in words.
          <div className="home-status__bar" aria-hidden="true">
            {(
              [
                ['done', status.completed],
                ['work', status.inWork],
                ['idle', status.notStarted],
              ] as const
            ).map(([tone, n]) =>
              n > 0 ? (
                <span
                  key={tone}
                  className={`home-status__seg home-status__seg--${tone}`}
                  style={{ flexGrow: n }}
                />
              ) : null,
            )}
          </div>
        )}
        <dl className="home-status">
          <div className="home-status__item home-status__item--done">
            <dt>
              <Icon name="check" /> הושלמו
            </dt>
            <dd>{status.completed}</dd>
          </div>
          <div className="home-status__item home-status__item--work">
            <dt>
              <Icon name="clock" /> בעבודה
            </dt>
            <dd>{status.inWork}</dd>
          </div>
          <div className="home-status__item home-status__item--idle">
            <dt>
              <Icon name="circle" /> טרם התחילו
            </dt>
            <dd>{status.notStarted}</dd>
          </div>
          <div className="home-status__item home-status__item--defect">
            <dt>
              <Icon name="alert" /> ליקויים פתוחים
            </dt>
            <dd>{status.openDefects}</dd>
          </div>
        </dl>
      </div>
      {action > 0 ? (
        <p className="notice notice--warning home-status__action">
          דורש טיפול:{' '}
          {[
            status.refused > 0
              ? status.refused === 1
                ? 'מסירה אחת שהרוכש סירב לחתום עליה'
                : `${String(status.refused)} מסירות שהרוכש סירב לחתום עליהן`
              : '',
            status.awaitingReinspection > 0
              ? `${String(status.awaitingReinspection)} ליקויים שממתינים לבדיקה חוזרת`
              : '',
          ]
            .filter((t) => t !== '')
            .join(' · ')}
        </p>
      ) : (
        status.apartments > 0 && <p className="hint">אין כרגע מסירות שדורשות טיפול.</p>
      )}
    </section>
  );
}

/** For the business Super Admin: what the project still needs before its first handover. */
function ReadinessCard({ projectId }: { readonly projectId: string }) {
  const [list] = useLoad(`home-readiness:${projectId}`, () =>
    apiGet('/project-admin', projectAdminListResponseSchema),
  );
  if (list.state !== 'ready') return null;
  const project = list.data.items.find((p) => p.id === projectId);
  if (project === undefined) return null;
  const missing = readinessChecks(project.setup).filter((c) => !c.done && !c.optional);
  return (
    <section className="home-section" aria-labelledby="home-readiness-title">
      <div className="home-section__head">
        <h2 id="home-readiness-title">מוכנות הפרויקט</h2>
      </div>
      <div className="home-card home-readiness">
        {missing.length === 0 ? (
          <p>
            <Icon name="check" /> הפרויקט מוכן למסירות.
          </p>
        ) : (
          <>
            <p>לפני המסירה הראשונה חסרים:</p>
            <ul>
              {missing.map((c) => (
                <li key={c.id}>{c.label}</li>
              ))}
            </ul>
            <AppLink className="button" to="#master-data">
              להשלמה בהקמת הפרויקט
            </AppLink>
          </>
        )}
      </div>
    </section>
  );
}
